import { Typography } from '@euler/app/components/typography';
import { CardLoading } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiDashboardGrantInput,
  BiDashboardGrantPermission,
  BiDashboardVisibility,
  kBiMaxGrantsPerDashboard,
} from '@euler/model/bi/dashboard';
import { Lock, Users } from 'lucide-react-native';
import { memo, useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiButton } from '../../components/ui/BiButton';
import { BiRetry } from '../../components/ui/BiRetry';
import { biErrorMessage } from '../../functions/biErrors';
import { AudienceConfirm } from './AudienceConfirm';
import { GrantRow } from './GrantRow';
import { MemberPicker } from './MemberPicker';
import { VisibilityOption } from './VisibilityOption';
import { biAudienceLines, biSameGrants } from './functions/biAudience';
import { useBiSharing } from './functions/useBiSharing';

/**
 * Sharing, for the owner: the broad audience (private or the whole store)
 * and named members who may view or edit. Saving first shows who will
 * reach the dashboard.
 */
export const SharingPanel = memo(
  ({
    visible,
    dashboardId,
    ownerMemberId,
    visibility,
    onClose,
    onSaved,
  }: {
    visible: boolean;
    dashboardId: string;
    ownerMemberId: number;
    visibility: BiDashboardVisibility;
    onClose: () => void;
    onSaved: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, retry, save } = useBiSharing(dashboardId, visible);
    const [draftVisibility, setDraftVisibility] = useState(visibility);
    const [grants, setGrants] = useState<BiDashboardGrantInput[]>([]);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const savedGrants = state.kind === 'ready' ? state.grants : null;
    useEffect(() => {
      if (!visible || !savedGrants) return;
      setDraftVisibility(visibility);
      setGrants(savedGrants);
      setConfirming(false);
      setError(null);
    }, [visible, savedGrants, visibility]);

    const members = useMemo(
      () => (state.kind === 'ready' ? state.members : []),
      [state],
    );
    const nameOf = (memberId: number) =>
      members.find(member => member.id === memberId)?.name ??
      `成员 ${memberId}`;
    const grantable = useMemo(
      () =>
        members.filter(
          member =>
            member.id !== ownerMemberId &&
            !grants.some(grant => grant.memberId === member.id),
        ),
      [members, grants, ownerMemberId],
    );
    const changed =
      !!savedGrants &&
      (draftVisibility !== visibility || !biSameGrants(savedGrants, grants));

    const confirm = async () => {
      if (!savedGrants) return;
      setBusy(true);
      setError(null);
      try {
        await save(
          { visibility, grants: savedGrants },
          { visibility: draftVisibility, grants },
        );
        onClose();
      } catch (e) {
        console.error('BI sharing save failed', dashboardId, e);
        setError(biErrorMessage(e, '没有保存成功，请稍后重试。'));
      } finally {
        setBusy(false);
        // The visibility may have landed even when the grants did not.
        onSaved();
      }
    };

    const setPermission = (
      memberId: number,
      permission: BiDashboardGrantPermission,
    ) =>
      setGrants(current =>
        current.map(grant =>
          grant.memberId === memberId ? { memberId, permission } : grant,
        ),
      );

    return (
      <BottomPanel visible={visible} onClose={onClose} tall>
        <View style={{ flex: 1, padding: rpx(24), gap: rpx(12) }}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            共享看板
          </Typography.Text>
          {state.kind === 'loading' ? (
            <CardLoading label="正在读取共享设置…" />
          ) : state.kind === 'failed' ? (
            <BiRetry onRetry={retry} />
          ) : confirming ? (
            <AudienceConfirm
              lines={biAudienceLines(
                draftVisibility,
                grants,
                nameOf,
                members.length,
              )}
              error={error}
              busy={busy}
              onBack={() => setConfirming(false)}
              onConfirm={confirm}
            />
          ) : (
            <>
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ gap: rpx(12) }}
                keyboardShouldPersistTaps="handled"
              >
                <VisibilityOption
                  icon={Lock}
                  title="私有"
                  detail="只有你和下面添加的成员能看到"
                  selected={draftVisibility === BiDashboardVisibility.Private}
                  onPress={() =>
                    setDraftVisibility(BiDashboardVisibility.Private)
                  }
                />
                <VisibilityOption
                  icon={Users}
                  title="门店可见"
                  detail="本店所有成员都能查看；要让同事编辑，在下面添加"
                  selected={draftVisibility === BiDashboardVisibility.Store}
                  onPress={() =>
                    setDraftVisibility(BiDashboardVisibility.Store)
                  }
                />
                <Typography.Text
                  medium
                  size={rpx(13)}
                  color={aiColors.foreground}
                >
                  指定成员
                </Typography.Text>
                {grants.map(grant => (
                  <GrantRow
                    key={grant.memberId}
                    name={nameOf(grant.memberId)}
                    permission={grant.permission}
                    onChange={permission =>
                      setPermission(grant.memberId, permission)
                    }
                    onRemove={() =>
                      setGrants(current =>
                        current.filter(
                          entry => entry.memberId !== grant.memberId,
                        ),
                      )
                    }
                  />
                ))}
                {grants.length < kBiMaxGrantsPerDashboard ? (
                  <MemberPicker
                    members={grantable}
                    onPick={member =>
                      setGrants(current => [
                        ...current,
                        {
                          memberId: member.id,
                          permission: BiDashboardGrantPermission.View,
                        },
                      ])
                    }
                  />
                ) : null}
              </ScrollView>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: rpx(8),
                }}
              >
                <BiButton title="取消" onPress={onClose} />
                <BiButton
                  title="保存"
                  tone="primary"
                  disabled={!changed}
                  onPress={() => setConfirming(true)}
                />
              </View>
            </>
          )}
        </View>
      </BottomPanel>
    );
  },
);
